import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MonitorSmartphone } from 'lucide-react'
import { useTRPC } from '../lib/trpc'
import { when } from '../lib/when'
import { Button, Card, FieldSection, SettingBlock } from './index'
import { useConfirm } from './useConfirm'

/**
 * Where this person is signed in.
 *
 * The act that matters is "everywhere else": a compromised password is
 * answered by ending the sessions it opened, and changing the password does
 * not do that on its own. Ending one at a time is the ordinary case — a
 * borrowed laptop, a phone you no longer have.
 */
export function Sessions() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { confirm, dialog } = useConfirm()

  const sessions = useQuery(trpc.auth.sessions.queryOptions())
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: trpc.auth.sessions.queryKey() })
  const end = useMutation(
    trpc.auth.endSession.mutationOptions({ onSuccess: invalidate }),
  )
  const endOthers = useMutation(
    trpc.auth.endOtherSessions.mutationOptions({ onSuccess: invalidate }),
  )

  const held = sessions.data ?? []
  const others = held.filter((s) => !s.current).length

  return (
    <>
      <FieldSection
        title="Where you are signed in"
        description="Each browser you have signed in from. Ending one signs it out the next time it asks for anything."
      >
        <div className="account__list">
          {held.map((s) => (
            <Card key={s.id}>
              <div className="account__row">
                <span className="account__icon" aria-hidden>
                  <MonitorSmartphone size={16} />
                </span>
                <div className="account__what">
                  <span className="account__name">
                    {browserOf(s.userAgent)}
                    {s.current ? ' · this one' : ''}
                  </span>
                  <span className="account__used">
                    Last used {when(s.lastSeenAt)}
                  </span>
                </div>
                {/* The one you are using has no end button: signing yourself out
                  is what the account menu is for, and putting it here reads as
                  the same act as ending somebody else's. */}
                {s.current ? null : (
                  <Button
                    variant="secondary"
                    onClick={() => end.mutate({ sessionId: s.id })}
                  >
                    End
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      </FieldSection>

      {others > 0 ? (
        <SettingBlock
          title="Sign out everywhere else"
          description={`${others} other ${others === 1 ? 'session ends' : 'sessions end'}. You stay signed in here. Do this if you think somebody else has your password — and change it afterwards.`}
        >
          <Button
            variant="danger"
            onClick={async () => {
              const ok = await confirm({
                title: 'Sign out everywhere else?',
                body: `${others} other ${others === 1 ? 'session ends' : 'sessions end'}. You stay signed in here. Do this if you think somebody else has your password — and change it afterwards.`,
                verb: 'Sign out everywhere else',
              })
              if (ok) endOthers.mutate()
            }}
          >
            Sign out everywhere else
          </Button>
        </SettingBlock>
      ) : null}
      {dialog}
    </>
  )
}

/**
 * A browser's name out of what it called itself.
 *
 * Deliberately shallow: a user-agent string cannot be parsed reliably and this
 * only has to be recognizable to the person who made it. Anything unmatched is
 * shown as itself rather than guessed at, because a wrong name is worse than an
 * ugly one when the next click ends a session.
 */
export function browserOf(agent: string | null): string {
  if (!agent) return 'An unnamed browser'
  const known = ['Firefox', 'Edg', 'Chrome', 'Safari']
  const hit = known.find((k) => agent.includes(k))
  const name = hit === 'Edg' ? 'Edge' : hit
  const os = ['iPhone', 'iPad', 'Android', 'Mac', 'Windows', 'Linux'].find(
    (o) => agent.includes(o),
  )
  if (!name) return agent.slice(0, 60)
  return os ? `${name} on ${os}` : name
}
